// src/data/totalExp.js
// Gesamt-EXP über alle Wochen (gleiche Regeln wie weekExp.js) und Sprachniveaus als Meilensteine.
// Quelle ist Supabase, damit die Zahl auf jedem Gerät gleich ist.

import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";
import { TARGET_WORDS } from "./learningConfig";
import { getWeeksTotal, getTranslationWeekTarget } from "./translationPlan";
import { getPhrasesPerWeek } from "./phraseSchedule";
import { loadProgressRemote } from "./progressStorage";
import { migrateFormKeys } from "./verbProgressStorage";
import {
  computeWeekExp,
  computeVerbsPercent,
  getGapsTargetForPace,
  vocabExpForBox,
} from "./weekExp";

// Sprachniveaus als Anteil der möglichen Kurs-EXP. Die EXP wachsen gleichmäßig mit den
// Wochen, der Anteil entspricht also etwa dem gelernten Wortschatz (3000 Wörter = 100 %):
// A1 ≈ 450, A2 ≈ 1050, B1 ≈ 1950 Wörter, B2 = Kursende.
export const LEVEL_MILESTONES = [
  { label: "A1", share: 0.15 },
  { label: "A2", share: 0.35 },
  { label: "B1", share: 0.65 },
  { label: "B2", share: 1 },
];

function weeklyWordsFor(pace) {
  return Math.ceil(TARGET_WORDS / getWeeksTotal(pace));
}

/** Höchstmögliche EXP der Woche `week` (nichts erledigt, nur die Ziele) */
function weekMaxExp(week, pace) {
  return computeWeekExp({
    pace,
    vocabTarget: weeklyWordsFor(pace),
    gapsTarget: getGapsTargetForPace(pace),
    phrasesTarget: getPhrasesPerWeek(pace),
    translationsTarget: getTranslationWeekTarget(week, pace),
  }).max;
}

/** Höchstmögliche EXP über den ganzen Kurs eines Tempos */
export function courseMaxExp(pace) {
  let sum = 0;
  for (let week = 1; week <= getWeeksTotal(pace); week++) sum += weekMaxExp(week, pace);
  return sum;
}

/**
 * Meilensteine und erreichtes Niveau für `total` EXP.
 * @returns {{ max, milestones: {label, at}[], reached: string|null, next: {label, at}|null }}
 */
export function levelInfo(total, pace) {
  const max = courseMaxExp(pace);
  const milestones = LEVEL_MILESTONES.map((m) => ({
    label: m.label,
    at: Math.round(max * m.share),
  }));
  const done = milestones.filter((m) => total >= m.at);
  return {
    max,
    milestones,
    reached: done.length ? done[done.length - 1].label : null,
    next: milestones.find((m) => total < m.at) || null,
  };
}

/**
 * Gesamt-EXP aus dem Fortschritt aller Wochen.
 * progress: Vokabel-Fortschritt { [wordId]: { box } }
 * verbStatesByWeek / gapsByWeek / phrasesByWeek / translationsByWeek: { [week]: … }
 */
export function computeTotalExp({
  pace,
  progress = {},
  verbStatesByWeek = {},
  gapsByWeek = {},
  phrasesByWeek = {},
  translationsByWeek = {},
}) {
  const weeks = getWeeksTotal(pace);
  const vocab = Object.values(progress).reduce(
    (sum, state) => sum + vocabExpForBox(state && state.box),
    0
  );

  let rest = 0;
  for (let week = 1; week <= weeks; week++) {
    const hasData =
      verbStatesByWeek[week] ||
      gapsByWeek[week] ||
      phrasesByWeek[week] ||
      translationsByWeek[week];
    if (!hasData) continue;
    const exp = computeWeekExp({
      pace,
      vocabTarget: 0,
      verbsPercent: computeVerbsPercent(verbStatesByWeek[week]),
      gapsDone: gapsByWeek[week],
      gapsTarget: getGapsTargetForPace(pace),
      phrasesDone: phrasesByWeek[week],
      phrasesTarget: getPhrasesPerWeek(pace),
      translationsDone: translationsByWeek[week],
      translationsTarget: getTranslationWeekTarget(week, pace),
    });
    rest += exp.total;
  }

  return Math.min(vocab + rest, courseMaxExp(pace));
}

async function loadWeekCounts(table, userId, profileId, languageId, pace) {
  const { data, error } = await supabase
    .from(table)
    .select("week_index, correct_count")
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .eq("language_id", languageId)
    .eq("pace", pace);
  if (error) throw error;
  const byWeek = {};
  (data || []).forEach((row) => {
    const value = Number(row.correct_count) || 0;
    byWeek[row.week_index || 1] = Math.max(byWeek[row.week_index || 1] || 0, value);
  });
  return byWeek;
}

/**
 * Lädt den Fortschritt aller Wochen aus Supabase und rechnet die Gesamt-EXP aus.
 * null = nicht eingeloggt oder Laden fehlgeschlagen.
 */
export async function loadTotalExp(languageId, pace, profileId = "default") {
  try {
    const user = (await getSessionUser()).data.user;
    if (!user) return null;

    const verbQuery = supabase
      .from("verb_progress")
      .select("verb_id, week_index, mode, table_good_runs, forms")
      .eq("user_id", user.id)
      .eq("profile_id", profileId)
      .eq("language_id", languageId);

    const [progress, verbs, gaps, phrases, translations] = await Promise.all([
      loadProgressRemote(user.id, languageId, profileId),
      verbQuery,
      loadWeekCounts("sentence_cloze_progress", user.id, profileId, languageId, pace),
      loadWeekCounts("phrase_progress", user.id, profileId, languageId, pace),
      loadWeekCounts("translation_progress", user.id, profileId, languageId, pace),
    ]);
    if (!progress || verbs.error) return null;

    const verbStatesByWeek = {};
    (verbs.data || []).forEach((row) => {
      const week = row.week_index || 1;
      verbStatesByWeek[week] = verbStatesByWeek[week] || {};
      verbStatesByWeek[week][row.verb_id] = {
        mode: row.mode || "table",
        tableGoodRuns: row.table_good_runs ?? 0,
        forms: migrateFormKeys(row.forms) || null,
      };
    });

    return computeTotalExp({
      pace,
      progress,
      verbStatesByWeek,
      gapsByWeek: gaps,
      phrasesByWeek: phrases,
      translationsByWeek: translations,
    });
  } catch (e) {
    console.warn("loadTotalExp failed:", e);
    return null;
  }
}
